import React from "react";
import { Box, Button, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import { parseJsonSafe } from "../../utils/metaMessageSocial";

/**
 * Extrai dados da publicação comentada a partir de json_whatsapp.meta_comment.
 */
export function getMetaCommentPostFromMessage(message) {
  const root = parseJsonSafe(message?.json_whatsapp);
  if (!root || typeof root !== "object") return null;
  const meta = root.meta_comment || root.metaComment;
  if (!meta || typeof meta !== "object") return null;

  const permalink = meta.permalink ? String(meta.permalink).trim() : "";
  const postCaption = meta.postCaption || meta.caption
    ? String(meta.postCaption || meta.caption).trim()
    : "";
  const mediaType = meta.mediaType ? String(meta.mediaType).trim() : "";
  // Nunca usar meta.username — esse campo é o autor do comentário, não da publicação.
  const postUsername = meta.postUsername
    ? String(meta.postUsername).trim()
    : "";
  const previewUrl =
    meta.postImageUrl ||
    meta.thumbnailUrl ||
    meta.previewUrl ||
    meta.mediaUrl ||
    "";
  const imageUrl = previewUrl ? String(previewUrl).trim() : "";

  const origin = root.xpro_origin ? String(root.xpro_origin) : "";
  const event = root.xpro_meta_event ? String(root.xpro_meta_event) : "";
  let platform = "meta";
  if (origin.includes("instagram") || event === "instagram_comment") {
    platform = "instagram";
  } else if (origin.includes("facebook") || event === "facebook_comment") {
    platform = "facebook";
  } else if (meta.mediaId && !meta.postId) {
    platform = "instagram";
  } else if (meta.postId) {
    platform = "facebook";
  }

  if (!permalink && !postCaption && !imageUrl) return null;

  return {
    permalink: permalink || null,
    postCaption: postCaption || null,
    mediaType: mediaType || null,
    postUsername: postUsername || null,
    imageUrl: imageUrl || null,
    platform,
  };
}

/**
 * Remove o apêndice textual da publicação (já renderizado no card).
 */
export function stripCommentPostAppendix(text) {
  const raw = text != null ? String(text) : "";
  if (!raw) return raw;
  const idx = raw.search(/\n\s*———\s*\n\s*📎 Publicação/);
  if (idx < 0) return raw;
  return raw.slice(0, idx).trimEnd();
}

const IG_GRADIENT =
  "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)";
const FB_BLUE = "#1877F2";

function AvatarBadge({ platform }) {
  const isIg = platform === "instagram";
  return (
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: isIg ? "50%" : 1,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        background: isIg ? IG_GRADIENT : FB_BLUE,
        color: "#fff",
        boxShadow: isIg ? "0 0 0 2px #fff, 0 0 0 3px #e1306c" : "none",
      }}
    >
      {isIg ? (
        <InstagramIcon sx={{ fontSize: 18 }} />
      ) : (
        <FacebookIcon sx={{ fontSize: 20 }} />
      )}
    </Box>
  );
}

function PostMedia({ imageUrl, platform }) {
  if (!imageUrl) {
    return (
      <Box
        sx={{
          height: 120,
          display: "grid",
          placeItems: "center",
          bgcolor: platform === "instagram" ? "#fafafa" : "#f0f2f5",
          color: "text.secondary",
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor: "rgba(0,0,0,0.08)",
        }}
      >
        <Typography variant="caption">Sem prévia da mídia</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        bgcolor: "#000",
        // Instagram ~ quadrado; Facebook um pouco mais largo
        aspectRatio: platform === "instagram" ? "1 / 1" : "16 / 10",
        maxHeight: platform === "instagram" ? 280 : 220,
        overflow: "hidden",
      }}
    >
      <Box
        component="img"
        src={imageUrl}
        alt="Publicação comentada"
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    </Box>
  );
}

function InstagramPostCard({ post }) {
  const handle = post.postUsername
    ? String(post.postUsername).replace(/^@/, "")
    : null;
  const caption =
    post.postCaption && post.postCaption.length > 280
      ? `${post.postCaption.slice(0, 280)}…`
      : post.postCaption;

  return (
    <Box
      sx={{
        mb: 1,
        maxWidth: 320,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#fff",
        color: "#262626",
        border: "1px solid #dbdbdb",
        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.25,
          py: 1,
          borderBottom: "1px solid #efefef",
        }}
      >
        <AvatarBadge platform="instagram" />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 700,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {handle ? handle : "Instagram"}
          </Typography>
          <Typography sx={{ fontSize: "0.6875rem", color: "#8e8e8e", lineHeight: 1.2 }}>
            Publicação no Instagram
          </Typography>
        </Box>
        <InstagramIcon sx={{ fontSize: 18, color: "#8e8e8e" }} />
      </Box>

      <PostMedia imageUrl={post.imageUrl} platform="instagram" />

      <Box sx={{ px: 1.25, pt: 1, pb: 0.5 }}>
        {caption ? (
          <Typography
            sx={{
              fontSize: "0.8125rem",
              lineHeight: 1.4,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              mb: 1,
            }}
          >
            {handle ? (
              <Box component="span" sx={{ fontWeight: 700, mr: 0.6 }}>
                {handle}
              </Box>
            ) : null}
            {caption}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: "0.75rem", color: "#8e8e8e", mb: 1 }}>
            Comentário em publicação do Instagram
          </Typography>
        )}

        {post.permalink ? (
          <Button
            component="a"
            href={post.permalink}
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
            size="small"
            variant="contained"
            endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            sx={{
              mb: 1,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 1.5,
              background: IG_GRADIENT,
              boxShadow: "none",
              "&:hover": {
                background: IG_GRADIENT,
                filter: "brightness(0.95)",
                boxShadow: "none",
              },
            }}
          >
            Abrir no Instagram
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}

function FacebookPostCard({ post }) {
  const pageName = post.postUsername
    ? String(post.postUsername).replace(/^@/, "")
    : "Facebook";
  const caption =
    post.postCaption && post.postCaption.length > 280
      ? `${post.postCaption.slice(0, 280)}…`
      : post.postCaption;

  return (
    <Box
      sx={{
        mb: 1,
        maxWidth: 340,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#fff",
        color: "#050505",
        border: "1px solid #ccd0d5",
        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
        fontFamily:
          'Helvetica, Arial, "Lucida Grande", sans-serif',
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.25,
          py: 1.1,
        }}
      >
        <AvatarBadge platform="facebook" />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 700,
              color: "#050505",
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {pageName}
          </Typography>
          <Typography sx={{ fontSize: "0.6875rem", color: "#65676b", lineHeight: 1.2 }}>
            Publicação no Facebook
          </Typography>
        </Box>
        <FacebookIcon sx={{ fontSize: 18, color: FB_BLUE }} />
      </Box>

      {caption ? (
        <Typography
          sx={{
            px: 1.25,
            pb: 1,
            fontSize: "0.875rem",
            lineHeight: 1.4,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {caption}
        </Typography>
      ) : null}

      <PostMedia imageUrl={post.imageUrl} platform="facebook" />

      <Box
        sx={{
          px: 1.25,
          py: 1,
          borderTop: "1px solid #e4e6eb",
          bgcolor: "#f7f8fa",
        }}
      >
        {post.permalink ? (
          <Button
            component="a"
            href={post.permalink}
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
            size="small"
            variant="contained"
            endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 1.5,
              bgcolor: FB_BLUE,
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#166fe5",
                boxShadow: "none",
              },
            }}
          >
            Abrir no Facebook
          </Button>
        ) : (
          <Typography sx={{ fontSize: "0.75rem", color: "#65676b" }}>
            Comentário em publicação do Facebook
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function MetaCommentPostCard({ post }) {
  if (!post) return null;
  if (post.platform === "facebook") {
    return <FacebookPostCard post={post} />;
  }
  // Instagram (default para comentários Meta)
  return <InstagramPostCard post={post} />;
}
